import hyperform from 'hyperform';
import $ from 'jquery';
import ElementValidityState, { IElementValidityState } from './validity-state';
import { isValidDate } from '../../utils/util.date';

/**
 * Notes for HyperFormJS
 * - 'data-custom-error' message doesn't show automatically, it needs to be set by setCustomValidity()
 * - 'minlength' and 'maxlenght' doesn't work when 'pattern' is present
 * - Registering custom validator function to an element will override all validation attributes except 'required'
 */

enum ValidationMessageAttribute {
    InvalidValue = 'data-invalid-value',
    ValueMissing = 'data-value-missing',
    CustomError = 'data-custom-error',
    PatternMismatch = 'data-pattern-mismatch',
}

export default class BookingValidation {

    public hyperformInstance;

    private readonly dateValidationFormat = 'dd/MM/yyyy';
    private readonly clientSideValidationMessageClass = 'hf-validation-error';
    private readonly validationContainerClass = 'validation-container';

    constructor(elementToEnhance: HTMLFormElement | Window) {
        // Remove server-side warning before appending HyperFormJS warning
        hyperform.setRenderer('attachWarning', (warning: HTMLElement, element: HTMLElement) => {
            const name = $(element).prop('name');
            const inputContainer = $(element).closest('.form-group, .validation-inputs');

            // Try to find validation message container with the same name
            const validationContainer = inputContainer.find(`.${this.validationContainerClass}[data-name="${name}"]`);

            if (validationContainer.length > 0) {
                validationContainer.html(warning);
            } else {
                // Use any validation message container found
                inputContainer.find(`.${this.validationContainerClass}`).html(warning);
            }

        });

        this.hyperformInstance = hyperform(elementToEnhance, {
            classes: {
                warning: `validation-error ${this.clientSideValidationMessageClass}`,
                invalid: 'invalid',
            },
        });
    }

    public destroy() {
        this.hyperformInstance.destroy();
    }

    public attachDateRangeValidator(input: HTMLInputElement) {

        hyperform.addValidator(input, (element: HTMLElement) => {
            return this.markInputElementValidity(this.dateRangeValidator(element as HTMLInputElement));
        });

    }

    public attachDateValidValidator(input: HTMLInputElement) {

        hyperform.addValidator(input, (element: HTMLElement) => {
            return this.markInputElementValidity(this.dateValidValidator(element as HTMLInputElement));
        });
    }

    public attachDateNotExpiredValidator(input: HTMLInputElement) {

        hyperform.addValidator(input, (element: HTMLElement) => {
            return this.markInputElementValidity(this.dateNotExpiredValidator(element as HTMLInputElement));
        });

    }

    public attachConditionalRequiredValidator(checker: HTMLInputElement, checkedElements: HTMLInputElement[]) {

        checkedElements.forEach((checked, index) => {
            hyperform.addValidator(checked, (element: HTMLElement) => {
                return this.markInputElementValidity(this.conditionalRequiredValidator(element as HTMLInputElement, checker));
            });
        });

    }

    public attachVerifyStringValidator(verifyStringClassName: string, mainStringClassName: string) {
        hyperform.addValidator(
            document.getElementsByClassName(verifyStringClassName)[0] as HTMLElement,
            (element: HTMLElement) => {
                var mainElement = document.getElementsByClassName(mainStringClassName)[0] as HTMLElement;
                var validityState = this.verifyStringValidator(element, mainElement);
                return this.markInputElementValidity(validityState);
            }
        )
    }

    public attachConditionalPatternValidator(input: HTMLInputElement, shouldValidate: () => boolean) {
        hyperform.addValidator(input, (element: HTMLElement) => {
            // if we shouldn't validate, return true
            return shouldValidate() ? input.validity.valid : true;
        });
    }

    public verifyStringValidator(verifyElement: HTMLElement, mainElement: HTMLElement) {
        var el = verifyElement as HTMLInputElement;
        var other = mainElement as HTMLInputElement;
        var valid = el.value.localeCompare(other.value, undefined, { sensitivity: 'accent' }) === 0;
        var validityState = new ElementValidityState(
            valid,
            el,
            this.getValidationMsgFromAttribute(el, ValidationMessageAttribute.CustomError)
        );
        return validityState;
    }

    public attachVerifyPhoneNumber() {
        hyperform.addValidator(
            document.getElementsByClassName('js-validate-phone')[0] as HTMLElement,
            (element: HTMLElement) => {
                const phoneTextBox = $(element).closest('.phonenumber');
                return phoneTextBox.phoneNumber().valid();
            }
        )
    }

    /** Behaviour validators */

    public dateValidValidator(element: HTMLInputElement): IElementValidityState {
        const formatArr = this.dateValidationFormat.split('/')
        const tempArr = element.value.split('/')
        const dateArr: string[] = []
        formatArr.forEach((fmt, idx) => {
            if (fmt == 'dd') {
                dateArr.push(tempArr[idx])
            }
            if (fmt == 'MM') {
                dateArr.push(tempArr[idx])
            }
            if (fmt == 'yyyy') {
                dateArr.push(tempArr[idx])
            }
        })

        // check all date element must present
        if (dateArr[0] && dateArr[1] && dateArr[2]) {
            // check is valid date
            const day = parseInt(dateArr[0])
            const month = parseInt(dateArr[1])
            const year = parseInt(dateArr[2])
            if (!isValidDate(day, month - 1, year)) {
                return new ElementValidityState(
                    false,
                    element,
                    this.getValidationMsgFromAttribute(element, ValidationMessageAttribute.InvalidValue),
                );
            }
        }

        return new ElementValidityState(true, element);
    }

    public dateRangeValidator(element: HTMLInputElement): IElementValidityState {
        if (!this.inputCheckedOrHasInputValue(element)) {
            return new ElementValidityState(
                false,
                element,
                this.getValidationMsgFromAttribute(element, ValidationMessageAttribute.ValueMissing),
            );
        }

        const date = this.parseDate(element.value, this.dateValidationFormat);
        if (!date) {
            return new ElementValidityState(
                false,
                element,
                this.getValidationMsgFromAttribute(element, ValidationMessageAttribute.PatternMismatch),
            );
        }

        const start = this.parseDate(element.getAttribute('min'), this.dateValidationFormat);
        const end = this.parseDate(element.getAttribute('max'), this.dateValidationFormat);

        if (!this.checkWithinDateRange(date, start, end)) {
            return new ElementValidityState(
                false,
                element,
                this.getValidationMsgFromAttribute(element, ValidationMessageAttribute.CustomError),
            );
        }

        return new ElementValidityState(true, element);
    }

    public dateNotExpiredValidator(element: HTMLInputElement): IElementValidityState {
        if (!this.inputCheckedOrHasInputValue(element)) {
            return new ElementValidityState(
                false,
                element,
                this.getValidationMsgFromAttribute(element, ValidationMessageAttribute.ValueMissing),
            );
        }

        const date = this.parseDate(element.value, this.dateValidationFormat);
        if (!date) {
            return new ElementValidityState(
                false,
                element,
                this.getValidationMsgFromAttribute(element, ValidationMessageAttribute.PatternMismatch),
            );
        }

        const expiryDate = this.parseDate(element.getAttribute('min'), this.dateValidationFormat);
        if (!this.checkDateLaterThan(date, expiryDate)) { // Date is NOT later than the given expiry date
            return new ElementValidityState(
                false,
                element,
                this.getValidationMsgFromAttribute(element, ValidationMessageAttribute.CustomError),
            );
        }

        return new ElementValidityState(true, element);
    }

    public conditionalRequiredValidator(checkedElement: HTMLInputElement, checkerElement: HTMLInputElement) {
        const checkerEnabled = this.inputCheckedOrHasInputValue(checkerElement);

        if (checkerEnabled && !checkedElement.value) {
            return new ElementValidityState(
                false,
                checkedElement,
                this.getValidationMsgFromAttribute(checkedElement, ValidationMessageAttribute.ValueMissing),
            );
        }

        return new ElementValidityState(true, checkedElement);
    }

    /** Utility functions */
    public markInputElementValidity(validityState: IElementValidityState): boolean {
        if (validityState.valid) {
            validityState.element.setCustomValidity('');
            return true;
        } else {
            if (validityState.message) {
                validityState.element.setCustomValidity(validityState.message);
            }
            return false;
        }
    }

    private checkWithinDateRange(input: Date, start: Date, end: Date): boolean {
        return start <= input && input <= end;
    }

    private checkDateLaterThan(input: Date, expiryDate: Date): boolean {
        return input > expiryDate;
    }

    private getValidationMsgFromAttribute(element: HTMLInputElement, dataAttr: string): string {
        return element.getAttribute(dataAttr) || '';
    }

    /** Date.parseInvariant belong to WebForms */
    private parseDate(value: string | null, format: string): Date {
        return window.Date.parseInvariant(value, this.dateValidationFormat);
    }

    private getInputValue(element: HTMLInputElement): string | boolean | null {
        switch (element.type.toLowerCase()) {
            case 'radio':
            case 'checkbox':
                return element.checked;
            case 'text':
            case 'select-one':
            case 'select-multiple':
                return element.value;
            default:
                return null;
        }
    }

    private inputCheckedOrHasInputValue(element: HTMLInputElement): boolean {
        return !!this.getInputValue(element);
    }

}

////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////

/** Legacy Validation functions */
window.validateEnteredAndNotNameWatermark = (sender, args) => {
    const value = window.ValidatorTrim(args.Value);
    const matches = value.match(/(?=.*[a-z])/gi);
    args.IsValid = value && (value.length > 1) && (matches && matches.length > 1) && (value !== 'As shown in passport');
};

window.validateEmailAndVerifyEmailMatch = (sender, args) => {
    const emailAddressTextBox = window.$get(sender.emailTextBox);
    const verifyEmailAddressTextBox = window.$get(sender.verifyEmailTextBox);
    args.IsValid =
        verifyEmailAddressTextBox.value &&
        (emailAddressTextBox.value.toUpperCase() === verifyEmailAddressTextBox.value.toUpperCase());
};

window.validateContactDetails = () => {
    if (!window.Page_IsValid) {
        window.Page_ClientValidate('ContactDetails');
    }
};

window.validatePhoneNumberValidCharacters = (sender, args) => {
    const phoneTextBox = $('#' + sender.phoneTextBox).closest('.phonenumber');
    args.IsValid = phoneTextBox.phoneNumber().valid();
};

window.validateEnteredIfInsuranceChecked = (sender, args) => {
    const checkBox = window.$get('InsuranceSelectedRadioButtonYes');
    if (checkBox && checkBox.checked) {
        args.IsValid = (window.ValidatorTrim(args.Value) !== '' && window.ValidatorTrim(args.Value) !== '-----');
    } else {
        args.IsValid = true;
    }
};

window.validateInsuranceSelection = (sender, args) => {
    const yesRadio = window.$get('InsuranceSelectedRadioButtonYes');
    const noRadio = window.$get('InsuranceSelectedRadioButtonNo');

    args.IsValid = yesRadio.checked || noRadio.checked;
};
